import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import FinalDocument from '../components/FinalDocument';

export default function Admin() {
  const AUTH_KEY = 'ritual_vault_access';
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem(AUTH_KEY) === 'true'
  );
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');
  const [isDeleting, setIsDeleting] = useState(null);
  
  // State for viewing certificate
  const [certificateAnswers, setCertificateAnswers] = useState(null);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [sessRes, respRes] = await Promise.all([
        api.getSessions(),
        api.getStageResponses()
      ]);
      setSessions(sessRes.data || []);
      setResponses(respRes.data || []);
    } catch (error) {
      console.error("Failed to fetch admin data", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    const correctPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123';
    if (password === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem(AUTH_KEY, 'true');
      setAuthError('');
      setPassword('');
    } else {
      setAuthError('Incorrect passphrase. Access denied.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(AUTH_KEY);
    setSessions([]);
    setResponses([]);
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm("Are you sure? This will delete the session AND all associated responses.")) return;
    setIsDeleting(`session-${id}`);
    try {
      await api.deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      setResponses(prev => prev.filter(r => r.session_id !== id));
    } catch (error) {
      alert(`Deletion failed: ${error.message}`);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteResponse = async (id) => {
    if (!window.confirm("Delete this individual response? This cannot be undone.")) return;
    setIsDeleting(`response-${id}`);
    try {
      await api.deleteStageResponse(id);
      setResponses(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      alert(`Deletion failed: ${error.message}`);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleViewCertificate = (session) => {
    const sessionResponses = responses.filter(r => r.session_id === session.id);
    
    // Helper to safely extract responses based on stage numbering
    const getResp = (stageNum) => {
      const r = sessionResponses.find(r => r.stage_number === stageNum);
      return r ? r.user_response : '[Incomplete]';
    };

    setCertificateAnswers({
      domain: session.goal_domain,
      desire: getResp(1),
      exchange: getResp(2),
      deadline: getResp(3),
      plan: getResp(4),
      oath: getResp(5),
      ritual: getResp(6) // Added missing stage 6
    });
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString.replace(/ /g, 'T')), { addSuffix: true });
    } catch (e) {
      return 'recently';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800/80 shadow-2xl backdrop-blur-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-500/20 text-rose-500">
              <SafeIcon icon={FiIcons.FiLock} className="text-2xl" />
            </div>
            <h2 className="text-2xl font-serif text-white mb-2">Restricted Area</h2>
            <p className="text-neutral-400 text-sm">Enter the passphrase to access the administration panel.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter passphrase..." className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-rose-500/50 transition-all font-mono" autoFocus />
            {authError && <p className="text-rose-500 text-sm text-center font-mono">{authError}</p>}
            <button type="submit" disabled={!password.trim()} className="w-full py-3 bg-rose-500 text-white font-medium rounded-lg hover:bg-rose-600 transition-colors uppercase tracking-widest text-xs">Unlock Vault</button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {certificateAnswers && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-neutral-950/95 overflow-y-auto flex flex-col items-center py-10 px-4 print:static print:bg-transparent print:block print:p-0"
          >
            <div className="w-full max-w-4xl relative">
              <FinalDocument 
                answers={certificateAnswers} 
                isAdmin={true} 
                onClose={() => setCertificateAnswers(null)} 
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`max-w-5xl mx-auto pt-8 pb-24 px-2 ${certificateAnswers ? 'print:hidden' : ''}`}>
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="text-3xl font-serif text-rose-500 mb-3 flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiDatabase} /> Administrative Control
            </h2>
            <p className="text-neutral-400 text-sm">Manage sessions, print certificates, or prune data.</p>
          </div>
          <button onClick={handleLogout} className="px-4 py-2 text-sm text-neutral-500 hover:text-white border border-neutral-800 rounded-lg flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiLogOut} /> Lock Vault
          </button>
        </div>

        <div className="flex gap-4 mb-8 border-b border-neutral-800">
          <button onClick={() => setActiveTab('sessions')} className={`pb-4 px-2 text-sm font-mono transition-colors relative ${activeTab === 'sessions' ? 'text-rose-500' : 'text-neutral-500'}`}>
            SESSIONS ({sessions.length})
            {activeTab === 'sessions' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-500" />}
          </button>
          <button onClick={() => setActiveTab('responses')} className={`pb-4 px-2 text-sm font-mono transition-colors relative ${activeTab === 'responses' ? 'text-rose-500' : 'text-neutral-500'}`}>
            RESPONSES ({responses.length})
            {activeTab === 'responses' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-500" />}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <span className="w-8 h-8 border-2 border-rose-500/30 border-t-rose-500 rounded-full animate-spin"></span>
          </div>
        ) : (
          <div className="space-y-4">
            {activeTab === 'sessions' ? (
              sessions.map(session => (
                <div key={session.id} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-xl border border-neutral-800/50 bg-neutral-900/40">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono bg-neutral-800 text-neutral-300 px-2 py-1 rounded">ID: {session.id}</span>
                      <span className="text-xs text-neutral-500 flex items-center gap-1"><SafeIcon icon={FiIcons.FiClock} /> {formatDate(session.created_at)}</span>
                    </div>
                    <p className="text-neutral-200">Domain: <span className="text-rose-500/80 font-medium">{session.goal_domain}</span></p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-0">
                    <button 
                      onClick={() => handleViewCertificate(session)} 
                      className="px-4 py-2 bg-amber-500/10 text-amber-500 border border-amber-500/20 rounded-lg hover:bg-amber-500 hover:text-white transition-all flex items-center gap-2 text-sm"
                    >
                      <SafeIcon icon={FiIcons.FiFileText} />
                      View Certificate
                    </button>
                    <button 
                      onClick={() => handleDeleteSession(session.id)} 
                      disabled={isDeleting === `session-${session.id}`} 
                      className="px-4 py-2 bg-rose-500/10 text-rose-500 border border-rose-500/20 rounded-lg hover:bg-rose-500 hover:text-white transition-all flex items-center gap-2 disabled:opacity-50 text-sm"
                    >
                      {isDeleting === `session-${session.id}` ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiIcons.FiTrash2} />}
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              responses.map(response => (
                <div key={response.id} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-xl border border-neutral-800/50 bg-neutral-900/40">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="text-xs font-mono bg-neutral-800 text-neutral-300 px-2 py-1 rounded">ID: {response.id}</span>
                      <span className="text-xs font-mono bg-neutral-950 text-neutral-500 px-2 py-1 rounded">SID: {response.session_id}</span>
                      <span className="text-xs text-neutral-500 flex items-center gap-1"><SafeIcon icon={FiIcons.FiClock} /> {formatDate(response.created_at)}</span>
                    </div>
                    <h4 className="text-sm font-mono text-rose-500/80 mb-1">{response.stage_name}</h4>
                    <p className="text-neutral-300 text-sm italic border-l-2 border-neutral-700 pl-3 py-1">"{response.user_response}"</p>
                  </div>
                  <button onClick={() => handleDeleteResponse(response.id)} disabled={isDeleting === `response-${response.id}`} className="px-3 py-2 text-xs bg-rose-500/5 text-rose-500/60 border border-rose-500/10 rounded-lg hover:bg-rose-500 hover:text-white transition-all flex items-center gap-2 disabled:opacity-50 shrink-0">
                    {isDeleting === `response-${response.id}` ? <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiIcons.FiXCircle} />}
                    Delete Response
                  </button>
                </div>
              ))
            )}
            {(activeTab === 'sessions' ? sessions : responses).length === 0 && (
              <div className="text-center py-24 text-neutral-600 font-serif italic">The vault is currently empty.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}